import { zodResolver } from "@hookform/resolvers/zod";
import { convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { FilePenIcon, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import useApiRequest from "../../../../lib/hooks/useRequest";
import { convertHtmlToEditorState } from "../../../../lib/hooks/utils";
import {
  JobDetailsCustomerResponseDto,
  JobListCustomerResponseDto,
  JobStatus,
} from "../../../../lib/interfaces/customers/jobs";
import AddIcon from "../../../../ui/components/icons/add-icon";
import { Button } from "../../../../ui/components/ui/button";
import DataFetchParent from "../../../../ui/components/ui/data-fetch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../ui/components/ui/form";
import { Input } from "../../../../ui/components/ui/input";
import CreateDialog from "../../create/create-dialog";
import CreateEditor from "../../create/create-editor";
import DataUpload from "../../create/data-upload";

// Define your Zod schema

import { TemplateMockResponseDto } from "@/lib/interfaces/template";
import { JobFileResponseDto } from "@/lib/interfaces/utils";
import JobFileList from "../../file-list";

// Define your Zod schema

type OrderShortInfo = {
  title: string;
  description: string;
  id: string | undefined;
  status: JobStatus | undefined;
  maxUploadSize: number | undefined;
  files: JobFileResponseDto[];
};

export const CreateJob = () => {
  const { apiRequest } = useApiRequest();
  const [searchParams] = useSearchParams();
  const draftId = useMemo(() => searchParams.get("draftId"), [searchParams]);
  const templateId = useMemo(() => searchParams.get("templateId"), [searchParams]);
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState<OrderShortInfo>();

  const getInfo = useCallback(async () => {
    const emptyOrder = {
      title: "",
      description: "",
      id: undefined,
      status: undefined,
      maxUploadSize: undefined,
      files: [],
    };
    setIsLoading(true);
    if (draftId) {
      const res = await apiRequest<JobDetailsCustomerResponseDto>(`jobs/customer/${draftId}`, "GET");
      if (res.data) {
        setOrder({ ...res.data, id: draftId });
      } else {
        setOrder(emptyOrder);
      }
    } else if (templateId) {
      const data = await apiRequest<TemplateMockResponseDto>(`templates/customer/${templateId}/mock`, "GET");
      if (data.data) {
        setOrder({
          id: undefined,
          title: data.data.mockTitle,
          description: data.data.mockDescription,
          status: undefined,
          maxUploadSize: undefined,
          files: [],
        });
      } else {
        setOrder(emptyOrder);
      }
    } else {
      setOrder(emptyOrder);
    }

    setIsLoading(false);
  }, [apiRequest, templateId, draftId]);

  useEffect(() => {
    getInfo();
  }, [getInfo, templateId, draftId]);

  return (
    <>
      <DataFetchParent
        data={order}
        renderElement={(data: OrderShortInfo) => <CreateJobForm apiOrder={data} />}
        isLoading={isLoading}
      />
    </>
  );
};

function CreateJobForm({ apiOrder }: { apiOrder: OrderShortInfo }) {
  const { apiRequest } = useApiRequest();
  const [id, setId] = useState<{ id: string | undefined; isDraft: boolean }>();
  const navigate = useNavigate();
  const [editorState, setEditorState] = useState(() => convertHtmlToEditorState(apiOrder.description || ""));
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState<OrderShortInfo>(apiOrder);

  const formSchema = z.object({
    name: z
      .string()
      .max(45, {
        message: "Der Name darf höchstens 45 Zeichen lang sein",
      })
      .min(1, {
        message: "Name ist erforderlich",
      }),
    description: z.string().refine(
      () => {
        const htmlContentProfile = draftToHtml(convertToRaw(editorState.getCurrentContent()));
        return htmlContentProfile.length > 2;
      },
      { message: "Beschreibung ist erforderlich" }
    ),
    isDraft: z.boolean().default(false),
  });

  type FormSchema = z.infer<typeof formSchema>;

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: apiOrder.title || "",
      description: apiOrder.description || "",
      isDraft: false,
    },
  });

  const onSubmit = async (data: FormSchema) => {
    setSubmitting(true);
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    const htmlContent = draftToHtml(rawContentState);
    const req = {
      title: data.name,
      description: htmlContent,
      draft: data.isDraft,
    };
    let url = "";
    let method: "PATCH" | "POST" = "POST";
    let toastText = "Auftrag erfolgreich erstellt";

    if (!data.isDraft && order.id) {
      url = `jobs/customer/${order.id}/submit`;
    } else if (data.isDraft && order.id) {
      url = `jobs/customer/${order.id}/update-draft`;
      method = "PATCH";
      toastText = "Auftrag erfolgreich aktualisiert";
    } else {
      url = "jobs/customer";
    }
    const res = await apiRequest<JobListCustomerResponseDto>(url, method, {
      body: req,
      toast: {
        toastText,
      },
    });
    if (res) {
      setId({ id: res.data?._id, isDraft: data.isDraft });
    }
  };

  const onFilesUploaded = () => {
    setSubmitting(false);
    if (!id?.isDraft) {
      navigate(`/order/${id?.id}`);
    } else {
      navigate(`/order`);
    }
  };
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
          {/* <div className="flex flex-col gap-5 h-full"> */}
          <div className="flex justify-between">
            <div>
              <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">Neuer Auftrag</h1>
              <p className="text-[16px] text-[#888]">Erstelle einen neuen Auftrag</p>
            </div>
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                className="flex gap-4"
                disabled={submitting}
                onClick={() => {
                  form.setValue("isDraft", true);
                  form.handleSubmit(onSubmit)();
                }}
              >
                {submitting ? <LoaderCircle className="animate-spin" /> : <FilePenIcon />}
                <span>Als Entwurf speichern</span>
              </Button>
              <Button type="submit" className="flex gap-4" disabled={submitting}>
                {submitting ? <LoaderCircle className="animate-spin" /> : <AddIcon />}
                <span>Auftrag einreichen</span>
              </Button>
            </div>
          </div>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px]">
                <FormLabel className="text-[16px] font-bold text-[#12282A]">
                  Name des Auftrages* <span className="font-normal text-[#888]">(max. 45 Zeichen)</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Trage hier einen Namen für Deinen Auftrag ein."
                    className="w-full placeholder:text-[#888]"
                    onChange={(e) => {
                      field.onChange(e);
                      if (e.target.value.length > 45) {
                        form.setError("name", {
                          type: "max",
                          message: "Der Name darf höchstens 45 Zeichen lang sein",
                        });
                      } else {
                        form.clearErrors("name");
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px]">
                <FormLabel className="text-[16px] font-bold text-[#12282A]">Auftragsbeschreibung*</FormLabel>
                <div className="w-full">
                  <FormControl>
                    <CreateEditor editorState={editorState} setEditorState={setEditorState} />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
      <div className="flex flex-col gap-5">
        {order.files.length > 0 && order.id && (
          <div className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px]">
            <JobFileList
              files={order.files}
              id={order.id}
              type="customer"
              setReload={() => {}}
              deleteCallback={(filepath) => {
                setOrder({ ...order, files: order.files.filter((file) => file.path !== filepath) });
              }}
            />
          </div>
        )}
        <DataUpload
          type="customer"
          onFilesUploaded={onFilesUploaded}
          id={id?.id}
          remainingFileSize={order.maxUploadSize}
        />
      </div>
      {submitting && <CreateDialog />}
    </>
  );
}
